import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {VelocityTransitionGroup} from 'velocity-react';

import Animations from '~/constants/Animations';
import {printAreaSwitchSides, undoRedoIcon} from '~/constants/InlineSvgs';
import {VIEW_ID_MAP} from '~/constants/ProductConstants';
import {printAreaText, SLEEVE_VIEWS} from '~/components/CanvasToolbar';
import renderInlineSvg from '~/utils/renderInlineSvg';
import {vibrate} from '~/utils/vibrate';
import {
  DropDownMenu,
  MenuItem,
  OutsideClickListener
} from '~/components/DropDownMenu';
import {useNavigate} from 'react-router-dom';

import './index.scss';

const duration = 200;
const easing = 'ease-in-out';
const toolNameAnalytics = 'mobile_canvas_toolbar';
const showAnimation = {...Animations.fadeIn, duration, easing};
const hideAnimation = {...Animations.fadeOut, duration, easing};

const classHeader = 'ndx-MobileToolbar';

const propTypes = {
  actions: PropTypes.shape({
    changeView: PropTypes.func,
    redo: PropTypes.func,
    undo: PropTypes.func
  }),
  actives: PropTypes.shape({
    redo: PropTypes.bool,
    selectedViewId: PropTypes.number,
    undo: PropTypes.bool
  }),
  additionalPrintArea: PropTypes.string,
  changeViews: PropTypes.array,
  hasAcceptableViews: PropTypes.bool,
  isSleeveDecoEligible: PropTypes.bool,
  sleevePrintEnabled: PropTypes.bool
};

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
const MobileToolbar = props => {
  const {actions, actives, isSleeveDecoEligible, changeViews} = props;
  const {redo: isRedoable, selectedViewId, undo: isUndoable} = actives;
  const navigate = useNavigate();

  const [open, setOpen] = React.useState(false);

  const buttonAnchorRef = React.useRef(null);

  const toggleMenu = () => {
    vibrate();
    setOpen(prevOpen => !prevOpen);
  };

  const changeView = () => {
    vibrate();
    selectedViewId === VIEW_ID_MAP.front
      ? actions.changeView(VIEW_ID_MAP.back, {
          source: toolNameAnalytics
        })
      : actions.changeView(VIEW_ID_MAP.front, {
          source: toolNameAnalytics
        });
  };

  const undo = () => {
    vibrate();
    actions.undo();
  };

  const redo = () => {
    vibrate();
    actions.redo();
  };

  const onClickAdditionalPrintAreaButton = () => {
    navigate('/sleevePrintPricing');
  };

  const renderButton = (icon, isEnabled) => {
    return (
      <div
        className={buttonClasses(isEnabled)}
        dangerouslySetInnerHTML={renderInlineSvg(icon)}
      />
    );
  };

  const buttonClasses = isEnabled =>
    classnames(`${classHeader}-button`, {isDisabled: !isEnabled});

  const closeMenu = React.useCallback(() => setOpen(false), []);

  const handleViewSelect = viewId => {
    actions.changeView(viewId);
  };

  const renderChangeViews = () => {
    const {changeViews, sleevePrintEnabled, additionalPrintArea} = props;

    if (changeViews.length < 1) {
      return null;
    }

    const changeViewItems = changeViews
      .map(changeView => {
        if (SLEEVE_VIEWS.includes(changeView.text) && !sleevePrintEnabled) {
          return null;
        }

        return (
          <MenuItem
            onClick={() => handleViewSelect(changeView.id)}
            key={changeView.id}
            active={selectedViewId === changeView.id}>
            <div className="view-and-text">
              <div className="view">{changeView.image}</div>
              <div className="text">{changeView.text}</div>
            </div>
          </MenuItem>
        );
      })
      .filter(item => item != null);

    if (!sleevePrintEnabled) {
      changeViewItems.push(
        <MenuItem
          className={'printAreaText'}
          onClick={() => onClickAdditionalPrintAreaButton()}
          key={'PAT-mobile-uid'}>
          <div className="view-and-text">
            <div className="text">{printAreaText(additionalPrintArea)}</div>
          </div>
        </MenuItem>
      );
    }

    return changeViewItems;
  };

  return (
    <div className={`${classHeader} u-clickPassThrough`}>
      <div className={`${classHeader}-leftSideButtonsGroup`}>
        <VelocityTransitionGroup enter={showAnimation} leave={hideAnimation}>
          <div className={`${classHeader}-buttonContainer`} onClick={undo}>
            {renderButton(undoRedoIcon, isUndoable)}
          </div>
          {isRedoable && (
            <div className={`${classHeader}-buttonContainer`} onClick={redo}>
              {renderButton(undoRedoIcon, isRedoable)}
            </div>
          )}
        </VelocityTransitionGroup>
      </div>

      {isSleeveDecoEligible
        ? changeViews.length && (
            <div className={`${classHeader}-rightSideButtonsGroup`}>
              <div
                className={`${classHeader}-buttonContainer`}
                onClick={toggleMenu}
                ref={buttonAnchorRef}>
                <div
                  className={`${classHeader}-button`}
                  dangerouslySetInnerHTML={renderInlineSvg(
                    printAreaSwitchSides
                  )}
                />
              </div>
              <OutsideClickListener
                onOutsideClick={closeMenu}
                ref={[buttonAnchorRef]}>
                <DropDownMenu anchorEl={buttonAnchorRef.current} open={open}>
                  {renderChangeViews()}
                </DropDownMenu>
              </OutsideClickListener>
            </div>
          )
        : props.hasAcceptableViews && (
            <div className={`${classHeader}-rightSideButtonsGroup`}>
              <div
                className={`${classHeader}-buttonContainer`}
                onClick={changeView}>
                <div
                  className={`${classHeader}-button`}
                  dangerouslySetInnerHTML={renderInlineSvg(
                    printAreaSwitchSides
                  )}
                />
              </div>
            </div>
          )}
    </div>
  );
};

MobileToolbar.displayName = 'MobileToolbar';
MobileToolbar.propTypes = propTypes;

export default MobileToolbar;
